import { HorizontalStepper } from "@dune/ui";
import { useQueryState } from "nuqs";
import OfferSelection from "./OfferSelection";
import SubmissionDocuments from "./SubmissionDocuments";
import { SubmissionOperation } from "./SubmissionOperation";
import { SubmissionParticipants } from "./SubmissionParticipants";
import { useGetConstructionSiteDetails } from "../../../api/queries/constructionSite.queries";
import { useRequiredParams } from "../../../hooks/useRequiredParams";
import { useMe } from "../../../api/queries/users.queries";
import { ReferenceableFilesCountBanner } from "../../../component/constructionSites/ReferenceableFilesCountBanner";

const STEP_OFFER_SELECTION = 1;
const STEP_DOCUMENT_UPLOAD = 2;
const STEP_OPERATION = 3;
const STEP_PARTICIPANTS = 4;

const SUBMISSION_STEPS = [
	{
		step: STEP_OFFER_SELECTION,
		title: "Sélection des offres",
	},
	{
		step: STEP_DOCUMENT_UPLOAD,
		title: "Dépôt de documents",
	},
	{
		step: STEP_OPERATION,
		title: "Opération",
	},
	{
		step: STEP_PARTICIPANTS,
		title: "Intervenants",
	},
];

export const ProjectSubmissionContent = () => {
	const { constructionSiteId } = useRequiredParams<{
		constructionSiteId: string;
	}>();

	const { me } = useMe();

	const [currentStepIndex, setCurrentStepIndex] = useQueryState("step", {
		defaultValue: STEP_OFFER_SELECTION,
		parse: (value) => {
			const parsed = Number(value);
			if (Number.isNaN(parsed) || parsed < 1 || parsed > 4) {
				return STEP_OFFER_SELECTION;
			}
			return parsed;
		},
		serialize: (value) => String(value),
	});

	const { data: constructionSiteDetails } =
		useGetConstructionSiteDetails(constructionSiteId);

	const isUpdateDisabled =
		me?.role === "BROKER" &&
		constructionSiteDetails?.status !== "IN_PROGRESS" &&
		constructionSiteDetails?.status !== "TO_ANALYZE";

	return (
		<div className="flex flex-col gap-4">
			<HorizontalStepper
				steps={SUBMISSION_STEPS}
				currentStepIndex={currentStepIndex}
				setCurrentStepIndex={setCurrentStepIndex}
				parentPaddingOffsetPx={24}
				canNavigateFromTo={(from) => {
					if (from === STEP_OFFER_SELECTION) {
						return false;
					}
					return true;
				}}
			/>
			<ReferenceableFilesCountBanner
				constructionSiteId={Number(constructionSiteId)}
			/>
			{currentStepIndex === STEP_OFFER_SELECTION && (
				<OfferSelection
					handleNextStep={() => setCurrentStepIndex(STEP_DOCUMENT_UPLOAD)}
					isUpdateDisabled={isUpdateDisabled}
				/>
			)}
			{currentStepIndex === STEP_DOCUMENT_UPLOAD && (
				<SubmissionDocuments
					handlePreviousStep={() => setCurrentStepIndex(STEP_OFFER_SELECTION)}
					handleNextStep={() => setCurrentStepIndex(STEP_OPERATION)}
					isUpdateDisabled={isUpdateDisabled}
				/>
			)}
			{currentStepIndex === STEP_OPERATION && (
				<SubmissionOperation
					handlePreviousStep={() => setCurrentStepIndex(STEP_DOCUMENT_UPLOAD)}
					handleNextStep={() => setCurrentStepIndex(STEP_PARTICIPANTS)}
					disabledDocuments={isUpdateDisabled}
					disabledInputs={isUpdateDisabled}
				/>
			)}
			{currentStepIndex === STEP_PARTICIPANTS && (
				<SubmissionParticipants
					handlePreviousStep={() => setCurrentStepIndex(STEP_OPERATION)}
					disabledInputs={isUpdateDisabled}
					disabledDocuments={isUpdateDisabled}
				/>
			)}
		</div>
	);
};
